import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LEAGUE } from "../../config/league";
import type { TransactionDTO } from "../../shared/types";
import { DirectionTag, ErrorBanner, Spinner } from "../components/ui";
import { api } from "../lib/api";
import { managerColor } from "../lib/colors";
import { formatMoney, formatPct, returnColor, usePoll } from "../lib/format";

/** What each kind of move is called, and whether it took a name out of a slot. */
const KIND: Record<TransactionDTO["kind"], { label: string; exit: boolean }> = {
  draft: { label: "drafted", exit: false },
  pickup: { label: "picked up", exit: false },
  replacement: { label: "replacement", exit: false },
  trade_in: { label: "traded in", exit: false },
  discard: { label: "dropped", exit: true },
  trade_out: { label: "traded away", exit: true },
  acquired: { label: "acquired", exit: true },
  delisted: { label: "delisted", exit: true },
};

function shortDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export default function TransactionsPage() {
  const { data, error, loading } = usePoll(
    api.transactions,
    LEAGUE.leaderboardPollSec * 1000,
    [],
  );
  const [showDraft, setShowDraft] = useState(false);

  const days = useMemo(() => {
    const events = (data?.events ?? []).filter(
      (e) => showDraft || e.kind !== "draft",
    );
    const byDate = new Map<string, TransactionDTO[]>();
    for (const e of events) {
      const arr = byDate.get(e.date) ?? [];
      arr.push(e);
      byDate.set(e.date, arr);
    }
    return [...byDate.entries()];
  }, [data, showDraft]);

  const draftCount = (data?.events ?? []).filter((e) => e.kind === "draft").length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-fg">Transactions</h1>
          <p className="text-xs text-muted">
            Every move in the league. An exit banks its return in that slot; the
            name replacing it starts from its own price at 0%.
          </p>
        </div>
        <button
          onClick={() => setShowDraft((v) => !v)}
          className="rounded-lg border border-edge px-3 py-2 text-xs font-semibold text-muted hover:text-fg"
        >
          {showDraft ? "Hide" : "Show"} draft picks ({draftCount})
        </button>
      </div>

      {error && <ErrorBanner message={error} />}
      {loading && !data && <Spinner label="Loading transactions…" />}

      {data && days.length === 0 && (
        <div className="panel p-6 text-center text-sm text-faint">
          No moves yet this season.
        </div>
      )}

      {days.map(([date, events]) => (
        <section key={date} className="panel overflow-hidden">
          <header className="flex items-baseline justify-between border-b border-edge px-4 py-2">
            <h2 className="num text-sm font-semibold uppercase tracking-widest text-clock">
              {shortDate(date)}
            </h2>
            <span className="num text-[10px] text-faint">
              {events.length} {events.length === 1 ? "move" : "moves"}
            </span>
          </header>
          <ul className="divide-y divide-edge/60">
            {events.map((e) => {
              const kind = KIND[e.kind];
              return (
                <li
                  key={e.id}
                  className="num flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-sm"
                >
                  <Link
                    to={`/manager/${e.manager}`}
                    className="w-20 shrink-0 font-semibold hover:underline"
                    style={{ color: managerColor(e.seed) }}
                  >
                    {e.manager}
                  </Link>
                  <span className="w-10 shrink-0 text-[10px] text-faint">
                    slot {e.slotIndex}
                  </span>
                  <span className="w-16 shrink-0 font-bold text-fg">{e.ticker}</span>
                  <DirectionTag direction={e.direction} size="sm" />
                  <span className={kind.exit ? "text-muted" : "text-gain/80"}>
                    {kind.label}
                  </span>
                  <span className="text-faint">
                    {e.price != null ? `at ${formatMoney(e.price)}` : ""}
                  </span>
                  {e.realizedReturn !== null && (
                    <span
                      className={`ml-auto shrink-0 font-semibold ${returnColor(e.realizedReturn)}`}
                    >
                      {formatPct(e.realizedReturn)} locked
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}
